import React, { useState, useEffect, useRef, Dispatch, ChangeEvent } from "react";
import { isEmpty } from "lodash";
import { Field, reduxForm } from "redux-form";
import RenderTypeahead from "../../../shared-components/Typeahead";
import { isValidDate } from "../../../helpers/util-common";
import ReduxDatePicker from "../../../shared-components/DatePicker";
import { BallotStatus, AdvanceSearchFormType } from "../../../models/common.models";
import commonActions from "../../../common.actions";
import { useDispatch } from "react-redux";

type Option = {
    text: string;
    value: string;
    key: string;
  };
interface Props {
    onAdvanceSearchSubmit: (formData: any, type: string) => {};
    toggleAdvanceSearchVisibility?: () => void;
    committeeData: any;
    ballotDesignationData: any;
    setAdvBallotSearchFormData: any;
    ballotAdvanceSearchFormData: any;
    ballotGlobalSearchTextRef: any;
    ballotStatusOption: Option[];
    onGlobalSearchSubmit: (option: string, text: string) => void;
}

const AdvanceSearchBallot: React.FC<Props> = (props) => {
    const dispatch = useDispatch();
    const { onAdvanceSearchSubmit, committeeData, ballotDesignationData, ballotGlobalSearchTextRef } = props;
    const [selectedBallotDesignation, setSelectedBallotDesignation] = useState<Array<string>>([]);
    const [selectedCommitteeSubcommitteeSection, setSelectedCommitteeSubcommitteeSection] = useState<Array<string>>([]);
    const advSearchButtonRef = useRef<HTMLButtonElement>(null);
    const [selectedBallotStatus, setSelectedBallotStatus] = useState(props.ballotAdvanceSearchFormData?.selectedBallotStatus || "all");
    const [fromDate, setFromDate] = useState<string | Date>(props.ballotAdvanceSearchFormData?.fromDate
        ? new Date(props.ballotAdvanceSearchFormData?.fromDate)
        : null);
    const [toDate, setToDate] = useState<string | Date>(props.ballotAdvanceSearchFormData?.toDate
        ? new Date(props.ballotAdvanceSearchFormData?.toDate)
        : null);
    const [clearAllTypeaheds, setClearAllTypeaheds] = useState<boolean>(false);
    // this will be modified when we change the date range implementation in future
    const [dateRangeType, setDateRangeType] = useState<string>('closeDate');
    const [formData, setFormData] = useState({});
    const [invalidFromDate, setInvalidFromDate] = useState(false);
    const [customClass, setCustomClass] = useState("advance-ballot-search");

    useEffect(() => {
        const formFieldData: any = {};        
        if (props.ballotAdvanceSearchFormData?.selectedBallotDesignation) {
            let selectedBallotDesignationFormData = props.ballotAdvanceSearchFormData.selectedBallotDesignation;
            setSelectedBallotDesignation(selectedBallotDesignationFormData);
            formFieldData.selectedBallotDesignation = selectedBallotDesignationFormData;
        }
        if (props.ballotAdvanceSearchFormData?.selectedCommitteeSubcommitteeSection) {
            let selectedCommitteeFormData = props.ballotAdvanceSearchFormData.selectedCommitteeSubcommitteeSection;
            setSelectedCommitteeSubcommitteeSection(selectedCommitteeFormData);
            formFieldData.selectedCommitteeSubcommitteeSection = selectedCommitteeFormData;
        }
        if (props.ballotAdvanceSearchFormData?.selectedBallotStatus) {
            let selectedBallotStatusFormData = props.ballotAdvanceSearchFormData.selectedBallotStatus;
            formFieldData.selectedBallotStatus = selectedBallotStatusFormData;
        }
        if (props.ballotAdvanceSearchFormData?.fromDate) {
            formFieldData.fromDate = props.ballotAdvanceSearchFormData.fromDate;
            formFieldData.dateRangeType = 'closeDate';
        }
        if (props.ballotAdvanceSearchFormData?.toDate) {
            formFieldData.toDate = props.ballotAdvanceSearchFormData.toDate;
            formFieldData.dateRangeType = 'closeDate';
        }
        setFormData(formFieldData);
    }, []);

    const addRemoveTypeaheadClass = (event: any) => {
        const keysToAddClassOn = ["Enter", "Backspace"];
        if (event.key === "ArrowDown") {
            event.target.value && setCustomClass("");
        }
        else if (keysToAddClassOn.indexOf(event.key) > -1) {
            setCustomClass("advance-ballot-search");
        }
    }

    const handleAdvSearchBallotInputKeyPress = (ev: any, textValue: string) => {
        if (clearAllTypeaheds) {
            setClearAllTypeaheds(false);
        }
        addRemoveTypeaheadClass(ev);
        if (ev.key === "Enter") {
            if (!isEmpty(ev.target.value)) {
                const filteredData = ballotDesignationData.filter((item: any) => item.toLowerCase().includes(ev.target.value.toLowerCase()));
                if (filteredData.length === 0) {
                    if (advSearchButtonRef.current) {
                        advSearchButtonRef.current.click();
                    }
                } else {                    
                    for (let i = 0; i < filteredData.length; i++) {
                        if (!selectedBallotDesignation.includes(filteredData[i])) {
                            setSelectedBallotDesignation((prev) => {
                                setFormData((prevState) => {
                                    return { ...prevState, selectedBallotDesignation: [...prev, filteredData[i]] };
                                });
                                return [...prev, filteredData[i]];
                            });
                            return;
                        }
                    }
                }
            } else if (!textValue) {
                if (advSearchButtonRef.current) {
                    advSearchButtonRef.current.click();
                }
            }
        }
    };

    const handleAdvSearchCommitteeInputKeyPress = (ev: any, textValue: string) => {
        if (clearAllTypeaheds) {
            setClearAllTypeaheds(false);
        }
        addRemoveTypeaheadClass(ev);
        if (ev.key === "Enter") {
            setCustomClass("advance-ballot-search");
            if (!isEmpty(ev.target.value)) {                
                const filteredData = committeeData.filter((item: any) => item.toLowerCase().includes(ev.target.value.toLowerCase()));
                if (filteredData.length === 0) {
                    if (advSearchButtonRef.current) {
                        advSearchButtonRef.current.click();
                    }
                } else {
                    for (let i = 0; i < filteredData.length; i++) {
                        if (!selectedCommitteeSubcommitteeSection.includes(filteredData[i])) {
                            setSelectedCommitteeSubcommitteeSection((prev) => {
                                setFormData((prevState) => {
                                    return { ...prevState, selectedCommitteeSubcommitteeSection: [...prev, filteredData[i]] };
                                });
                                return [...prev, filteredData[i]];
                            });
                            return;
                        }
                    }
                }
            } else if (!textValue) {
                if (advSearchButtonRef.current) {
                    advSearchButtonRef.current.click();
                }
            }
        }
    };

    const handlFromAndToDateChangeRaw = (event: any, field: string | Date) => {
        // This code will be added after one round of discussion
        // event?.key === "Enter" && field && advSearchButtonRef.current.click();
    }

    const handlFromAndToDateChange = (value: string, callback: Dispatch<string | Date | null>, field: string) => {
        let date = value;
        callback(date);
        setFormData(prev => {
            return {...prev, [field]: date, dateRangeType: 'closeDate'}
        });
    }

    const submitAdvanceSearchForm = () => {
        if (fromDate && new Date(fromDate) && toDate && new Date(toDate) && (new Date(fromDate).setHours(0, 0, 0, 0) > new Date(toDate).setHours(0, 0, 0, 0)) ) {
            setInvalidFromDate(true);
            return;
        }
        setInvalidFromDate(false);
        if (ballotGlobalSearchTextRef.current) {
            ballotGlobalSearchTextRef.current.value = "";
            props.onGlobalSearchSubmit("","");
        }
        props.setAdvBallotSearchFormData(formData);
        props.toggleAdvanceSearchVisibility && props.toggleAdvanceSearchVisibility();
        onAdvanceSearchSubmit(formData, AdvanceSearchFormType.Ballot);
    };
    const resetSearch = () => {
        setSelectedCommitteeSubcommitteeSection([]);
        setSelectedBallotDesignation([]);
        setSelectedBallotStatus("all");
        setFromDate(null);
        setToDate(null);
        setFormData({});
        setInvalidFromDate(false);
        setClearAllTypeaheds(true);
        props.setAdvBallotSearchFormData({});
        onAdvanceSearchSubmit({}, AdvanceSearchFormType.Ballot);
        dispatch(commonActions.setQuickSearchSelectedBallotStatus("All"));
    };
    
    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <div className="form-group">
                        <label htmlFor="ballotDesignation">Ballot Designation</label>
                        {ballotDesignationData && (
                            <div className="form-group">
                                <Field
                                    name="AdvBallotBallotDesignation"
                                    clearTypeahead={clearAllTypeaheds}
                                    id="AdvBallotBallotDesignation"
                                    value={selectedBallotDesignation}
                                    disabled={!ballotDesignationData?.length}
                                    component={RenderTypeahead}
                                    placeholder={ballotDesignationData?.length ? "Type here to select" : "Fetching records..."}
                                    data-testid="AdvBallotBallotDesignationSearch"
                                    customClass={customClass}
                                    multiple
                                    options={ballotDesignationData}
                                    selected={selectedBallotDesignation}
                                    defaultValue={selectedBallotDesignation}
                                    change={(value: string[]) => {
                                        setCustomClass("advance-ballot-search");
                                        if (clearAllTypeaheds) {
                                            setClearAllTypeaheds(false);
                                        }
                                        setFormData((prevState) => {
                                           return { ...prevState, selectedBallotDesignation: value };
                                        });
                                        setSelectedBallotDesignation(value);
                                    }}
                                    keyDown={handleAdvSearchBallotInputKeyPress}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-md-12">
                    <div className="form-group">
                        <label htmlFor="committeeDesignation">Committee Designation</label>
                        {committeeData && (
                            <div className="form-group">
                                <Field
                                    name="AdvBallotCommitteeDesignation"
                                    clearTypeahead={clearAllTypeaheds}
                                    id="AdvBallotCommitteeDesignation"
                                    component={RenderTypeahead}
                                    placeholder="Type here to select"
                                    customClass={customClass}
                                    multiple
                                    options={committeeData}
                                    selected={selectedCommitteeSubcommitteeSection}
                                    defaultValue={selectedCommitteeSubcommitteeSection}
                                    change={(value: string[]) => {
                                        setCustomClass("advance-ballot-search");
                                        if (clearAllTypeaheds) {
                                            setClearAllTypeaheds(false);
                                        }
                                        setFormData((prevState) => {
                                            return { ...prevState, selectedCommitteeSubcommitteeSection: value };
                                        });
                                        setSelectedCommitteeSubcommitteeSection(value);
                                    }}
                                    keyDown={handleAdvSearchCommitteeInputKeyPress}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label htmlFor="ballotStatus">Status</label>
                        <select
                            onChange={(e) => {
                                setSelectedBallotStatus(e.target.value);
                                setFormData((prev) => {
                                    return { ...prev, selectedBallotStatus: e.target.value };
                                });
                            }}
                            value={selectedBallotStatus}
                            className="custom-select astm-custom-select"
                            data-testid = "SelectStatus"
                        >
                            {props.ballotStatusOption?.map((status) => (
                              <option key={status.key} value={status.value}>
                                {status.text}
                              </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label htmlFor="dateRangeType">Date Range Type</label>
                        <select disabled value={dateRangeType} className="custom-select astm-custom-select">
                            <option key="closeDate" value="closeDate">
                                Close Date
                            </option>
                        </select>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <Field
                            component={ReduxDatePicker}
                            name="fromDate"
                            autoComplete="off"
                            label="From"
                            className="date-input"
                            placeholder="MM/DD/YYYY"
                            fieldError={invalidFromDate}
                            value={fromDate}
                            defaultValue={fromDate}
                            monthChangeHandler={(value: string) => handlFromAndToDateChange(value, setFromDate, 'fromDate')}
                            yearChangeHandler={(value: string) => handlFromAndToDateChange(value, setFromDate, 'fromDate')}
                            onChangeRawHandler={(event: ChangeEvent<HTMLInputElement>) =>  handlFromAndToDateChangeRaw(event, fromDate)}
                            changeHandler={(value: string) => handlFromAndToDateChange(value, setFromDate, 'fromDate')}
                        />
                        {invalidFromDate && <div id="helpText" className="invalid-feedback block">
                            {'From date should be less than the To date'}
                        </div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <Field
                            component={ReduxDatePicker}
                            name="toDate"
                            autoComplete="off"
                            label="To"
                            className="date-input"
                            placeholder="MM/DD/YYYY"
                            value={toDate}
                            defaultValue={toDate}
                            monthChangeHandler={(value: string) => handlFromAndToDateChange(value, setToDate, 'toDate')}
                            yearChangeHandler={(value: string) => handlFromAndToDateChange(value, setToDate, 'toDate')}
                            onChangeRawHandler={(event: ChangeEvent<HTMLInputElement>) =>  handlFromAndToDateChangeRaw(event, toDate)}
                            changeHandler={(value: string) => handlFromAndToDateChange(value, setToDate, 'toDate')}
                        />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12 mt16 adv-filter-buttons">
                    <button type="button" ref={advSearchButtonRef} className="btn btn-warning mr-2 astm-btn btn-primary" data-testid="submitAdvanceSearchFormButton" onClick={() => submitAdvanceSearchForm()}>
                        Search
                    </button>
                    <button onClick={resetSearch} data-testid="resetFormButton" className="btn astm-btn btn-light">
                        Reset
                    </button>
                </div>
            </div>
        </>
    );
};

//export default AdvanceSearchBallot;
export default reduxForm<{}, Props>({
    form: "AdvanceSearchBallot",
})(AdvanceSearchBallot);
